import React, { createContext, useContext, useState, useEffect } from 'react';
import { doc, getDoc, setDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from './AuthContext';

interface DrawAdminContextType {
  isAdmin: boolean;
  canInitiateDraw: boolean;
  canSetDrawTime: boolean;
  loading: boolean;
  isParticipationEnabled: boolean;
  toggleParticipation: () => Promise<void>;
}

const DrawAdminContext = createContext<DrawAdminContextType>({
  isAdmin: false,
  canInitiateDraw: false,
  canSetDrawTime: false,
  loading: true,
  isParticipationEnabled: true,
  toggleParticipation: async () => {}
});

export function DrawAdminProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [adminState, setAdminState] = useState({
    isAdmin: false,
    canInitiateDraw: false,
    canSetDrawTime: false,
    loading: true,
    isParticipationEnabled: false
  });

  useEffect(() => {
    const loadInitialState = async () => {
      try {
        const participationDoc = await getDoc(doc(db, 'drawSettings', 'participation'));
        if (participationDoc.exists()) {
          setAdminState(prev => ({
            ...prev,
            isParticipationEnabled: participationDoc.data().enabled
          }));
        }
      } catch (error) {
        console.error('Erreur lors du chargement de l\'état initial:', error);
      }
    };

    loadInitialState();

    const unsubscribe = onSnapshot(doc(db, 'drawSettings', 'participation'), (doc) => {
      if (doc.exists()) {
        setAdminState(prev => ({
          ...prev,
          isParticipationEnabled: doc.data().enabled
        }));
      }
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const checkAdminStatus = async () => {
      if (!user?.id) {
        setAdminState(prev => ({ ...prev, loading: false }));
        return;
      }

      try {
        const adminDoc = await getDoc(doc(db, 'drawAdmins', user.id));
        
        if (adminDoc.exists()) {
          const data = adminDoc.data();
          setAdminState(prev => ({
            ...prev,
            isAdmin: Boolean(data.isAdmin),
            canInitiateDraw: Boolean(data.canInitiateDraw),
            canSetDrawTime: Boolean(data.canSetDrawTime),
            loading: false
          }));
        } else {
          setAdminState(prev => ({
            ...prev,
            isAdmin: false,
            canInitiateDraw: false,
            canSetDrawTime: false,
            loading: false
          }));
        }
      } catch (error) {
        console.error('Erreur lors de la vérification admin:', error);
        setAdminState(prev => ({ ...prev, loading: false }));
      }
    };

    checkAdminStatus();
  }, [user?.id]);

  const toggleParticipation = async () => {
    if (!user?.id || !adminState.canInitiateDraw) return;
    
    try {
      const newState = !adminState.isParticipationEnabled;
      await setDoc(doc(db, 'drawSettings', 'participation'), {
        enabled: newState,
        lastUpdatedBy: user.id,
        lastUpdatedAt: new Date()
      });
      setAdminState(prev => ({ ...prev, isParticipationEnabled: newState }));
    } catch (error) {
      console.error('Erreur lors du changement d\'état:', error);
    }
  };

  return (
    <DrawAdminContext.Provider value={{
      ...adminState,
      toggleParticipation
    }}>
      {children}
    </DrawAdminContext.Provider>
  );
}

export const useDrawAdmin = () => {
  const context = useContext(DrawAdminContext);
  if (!context) {
    throw new Error('useDrawAdmin doit être utilisé à l\'intérieur de DrawAdminProvider');
  }
  return context;
}; 